import { Cookies } from "react-cookie";
import type { ApiShirt } from "../types/Shirt";
import { fetchShirtById, fetchSellerById } from "./shirtService";
import { auctionService } from "./auctionService";

/* ── New unified orders API ── */

export interface OrderShirt {
  id: string;
  brand: string;
  club: string;
  season: string;
  condition: string;
  size: string;
  primary_image_url: string | null;
  price?: number | null;
}

export interface CurrentOrderItem {
  type: string;
  sale_id: string;
  shirt: OrderShirt;
  shipping_status?: string;
  payment_status?: string;
  amount_due: number;
  created_at: string;
}

export interface CurrentOrdersResponse {
  action_required: CurrentOrderItem[];
  winning: CurrentOrderItem[];
  outbid: CurrentOrderItem[];
  problem?: CurrentOrderItem[];
}

export interface CompletedOrderItem {
  type: string;
  sale_id: string;
  shirt: OrderShirt;
  shipping_status?: string;
  payment_status?: string;
  amount_due: number;
  created_at: string;
  completed_at?: string | null;
}

export async function fetchCurrentOrders(): Promise<CurrentOrdersResponse> {
  const res = await fetch(`${API_URL}/orders/current`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch current orders: ${res.status}`);
  return res.json();
}

export async function fetchCompletedOrders(): Promise<CompletedOrderItem[]> {
  const res = await fetch(`${API_URL}/orders/completed`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch completed orders: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data)) return [];
  return data.map((item: Record<string, unknown>) => ({
    ...(item as unknown as CompletedOrderItem),
    amount_due: (item.amount_due ?? item.price) as number,
  }));
}

export interface SellerCurrentOrdersResponse {
  action_required: CurrentOrderItem[];
  awaiting_buyer_payment: CurrentOrderItem[];
  active_listings: CurrentOrderItem[];
  pending_approval: CurrentOrderItem[];
  rejected: CurrentOrderItem[];
  unsold: CurrentOrderItem[];
  problem?: CurrentOrderItem[];
}

export async function fetchSellerCurrentOrders(): Promise<SellerCurrentOrdersResponse> {
  const res = await fetch(`${API_URL}/orders/seller/current`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch seller current orders: ${res.status}`);
  const data = await res.json();
  // Normalize field names — each category uses different keys
  const norm = (items: Record<string, unknown>[]): CurrentOrderItem[] =>
    items.map((item) => ({
      ...(item as unknown as CurrentOrderItem),
      sale_id: (item.sale_id ?? item.auction_id ?? item.shirt_id) as string,
      amount_due: (item.amount_due ?? item.amount_earned ?? item.winning_bid ?? item.current_price ?? item.price) as number,
      created_at: (item.created_at ?? item.end_time ?? item.submitted_at) as string,
    }));
  return {
    action_required: norm(data.action_required ?? []),
    awaiting_buyer_payment: norm(data.awaiting_buyer_payment ?? []),
    active_listings: norm(data.active_listings ?? []),
    pending_approval: norm(data.pending_approval ?? []),
    rejected: norm(data.rejected ?? []),
    unsold: norm(data.unsold ?? []),
    problem: norm(data.problem ?? []),
  };
}

export async function fetchSellerCompletedOrders(): Promise<CompletedOrderItem[]> {
  const res = await fetch(`${API_URL}/orders/seller/completed`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch seller completed orders: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

const API_URL = process.env.REACT_APP_API_URL || "/api";

const cookies = new Cookies();

function getAccessToken(): string | null {
  const token = cookies.get("access_token");
  if (typeof token !== "string") return null;
  const t = token.trim();
  return t || null;
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Shape returned by the backend GET /orders/ endpoint */
export interface ApiOrder {
  id: string;
  shirt_id: string;
  seller_id: string; // Added seller_id to ApiOrder
  buyer_id: string;
  total_amount: number;
  status: string;
  payment_status: string;
  shipping_status: string;
  created_at: string;
  tracking_code?: string | null;
  tracking_number?: string | null;
  tracking_url?: string | null;
  shipping_label_url?: string | null;
  shipping_qr_url?: string | null;
  delivered_at?: string | null;
  // Auction-specific fields the backend may include
  auction_end_time?: string | null;
  sales_type?: "fixed_price" | "auction" | string | null;
  // Nested shirt summary embedded in the /details response
  shirt?: { id: string; title?: string | null; price?: number | null } | null;
  // Shipping address embedded in the /details response
  shipping?: {
    name?: string | null;
    street?: string | null;
    city?: string | null;
    postal?: string | null;
    country?: string | null;
  } | null;
}

/** Enriched order with shirt details attached */
export interface EnrichedOrder extends ApiOrder {
  shirt?: ApiShirt;
  seller?: {
    id: string;
    name: string;
  };
}

/** Fetch current user's purchases (bought orders). */
export async function fetchMyOrders(): Promise<ApiOrder[]> {
  const res = await fetch(`${API_URL}/orders/`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch orders: ${res.status}`);
  return res.json();
}

/** Fetch current user's sales (sold orders). */
export async function fetchMySales(): Promise<ApiOrder[]> {
  const res = await fetch(`${API_URL}/orders/sales/my`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch sales: ${res.status}`);
  return res.json();
}

/** Mark an order as shipped. */
export interface ShipOrderResponse {
  message?: string;
  shipping_status?: string;
  qr_code_url?: string;
  shipping_qr_url?: string;
  qr_url?: string;
  label_url?: string;
  label_pdf_url?: string;
  shipping_label_url?: string;
  label_download_url?: string;
  tracking_code?: string;
  tracking_number?: string;
  tracking_url?: string;
}

export async function shipOrder(
  orderId: string,
): Promise<ShipOrderResponse | null> {
  const res = await fetch(`${API_URL}/orders/${orderId}/ship`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
  });

  const raw = await res.text().catch(() => "");

  if (!res.ok) {
    // Try to extract a meaningful error message from the backend response
    let errorMessage = `Failed to ship order: ${res.status}`;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        errorMessage = parsed.detail || parsed.message || parsed.error || errorMessage;
      } catch {
        errorMessage = raw || errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  // Some backends return 204/no body for ship confirmation.
  if (!raw) return { shipping_status: "in_transit" };

  try {
    return JSON.parse(raw) as ShipOrderResponse;
  } catch {
    return { shipping_status: "in_transit" };
  }
}

export async function syncOrderPayment(
  orderId: string,
): Promise<ApiOrder | null> {
  const res = await fetch(`${API_URL}/orders/${orderId}/sync-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to sync payment: ${res.status}`);

  const raw = await res.text().catch(() => "");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiOrder;
  } catch {
    return null;
  }
}

export interface CaptureOrderPayload {
  order_id?: string;
  session_id?: string;
  payment_intent?: string;
}

export async function captureOrder(
  payload: CaptureOrderPayload,
): Promise<ApiOrder | null> {
  const res = await fetch(`${API_URL}/orders/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to capture order: ${res.status}`);

  const raw = await res.text().catch(() => "");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiOrder;
  } catch {
    return null;
  }
}

export async function releaseOrder(orderId: string): Promise<ApiOrder | null> {
  const res = await fetch(`${API_URL}/orders/release`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify({ order_id: orderId }),
  });
  if (!res.ok) throw new Error(`Failed to release order: ${res.status}`);

  const raw = await res.text().catch(() => "");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiOrder;
  } catch {
    return null;
  }
}

export async function updateOrderShippingStatus(
  orderId: string,
  status: "pending" | "label_created" | "in_transit" | "delivered" | "returned" | "problem",
): Promise<ApiOrder | null> {
  const res = await fetch(
    `${API_URL}/orders/update-shipping-status?order_id=${encodeURIComponent(orderId)}&status=${encodeURIComponent(status)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      credentials: "include",
    },
  );
  if (!res.ok)
    throw new Error(`Failed to update shipping status: ${res.status}`);

  const raw = await res.text().catch(() => "");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiOrder;
  } catch {
    return null;
  }
}

/** Refund a payment (full or partial). */
export async function refundOrder(orderId: string, reason: string): Promise<void> {
  const res = await fetch(`${API_URL}/orders/refund`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify({ order_id: orderId, reason }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail ?? `Refund mislukt (${res.status})`);
  }
}

/** Fetch order details. */
export async function fetchOrderDetails(orderId: string): Promise<ApiOrder> {
  const res = await fetch(`${API_URL}/orders/${orderId}/details`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch order details: ${res.status}`);
  const data = await res.json();
  // /details returns `price` instead of `total_amount`
  return {
    ...data,
    total_amount: data.total_amount ?? data.price ?? 0,
  };
}

/* ── User address ── */

export interface UserAddress {
  street: string | null;
  number: string | null;
  box: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  firstname: string | null;
  lastname: string | null;
}

/** Fetch the logged-in user's address from the auth endpoint. */
export async function fetchUserAddress(): Promise<UserAddress> {
  const res = await fetch(`${API_URL}/auth/user`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to fetch user: ${res.status}`);
  const data = await res.json();
  return {
    street: data.street ?? null,
    number: data.number ?? null,
    box: data.box ?? null,
    zip: data.zip ?? null,
    city: data.city ?? null,
    country: data.country ?? null,
    firstname: data.firstname ?? null,
    lastname: data.lastname ?? null,
  };
}

/* ── Checkout ── */

export interface ShippingAddress {
  shipping_name: string;
  shipping_street: string;
  shipping_number: string;
  shipping_box?: string;
  shipping_city: string;
  shipping_postal: string;
  shipping_country: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

/** Create an escrow checkout session for a fixed-price shirt. */
export async function createEscrowCheckout(
  shirtId: string,
  address: ShippingAddress,
): Promise<CheckoutSessionResponse> {
  const res = await fetch(`${API_URL}/orders/checkout-escrow`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify({
      shirt_id: shirtId,
      ...address,
      success_url: `${window.location.origin}/bestelling-bevestigd?source=stripe&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/cart`,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Checkout failed (${res.status}): ${text}`);
  }
  return res.json();
}

/**
 * Bevestig een fixed-price checkout vanaf de bestelbevestigingspagina.
 * Maakt server-side de Sale aan zodra de Stripe-sessie betaald is (fallback als
 * de webhook traag/gemist is). Idempotent: een al bestaande Sale wordt gewoon
 * teruggegeven.
 */
export async function confirmCheckout(
  sessionId: string,
): Promise<{ order_id?: string; payment_status?: string; created?: boolean }> {
  const res = await fetch(`${API_URL}/orders/confirm-checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Confirm checkout failed (${res.status}): ${text}`);
  }
  return res.json();
}

/**
 * Enrich orders with shirt data.
 * Fetches shirt details for each unique shirt_id.
 * Also fixes total_amount for auction orders where the backend returns 0.
 */
export async function enrichOrdersWithShirts(
  orders: ApiOrder[],
): Promise<EnrichedOrder[]> {
  const uniqueShirtIds = Array.from(new Set(orders.map((o) => o.shirt_id)));
  const uniqueSellerIds = Array.from(new Set(orders.map((o) => o.seller_id)));

  const shirtMap = new Map<string, ApiShirt>();
  const sellerMap = new Map<string, { id: string; name: string }>();

  await Promise.all([
    ...uniqueShirtIds.map(async (id) => {
      try {
        const shirt = await fetchShirtById(id);
        shirtMap.set(id, shirt);
      } catch {
        // Shirt might be deleted or inaccessible
      }
    }),
    ...uniqueSellerIds.map(async (id) => {
      try {
        const seller = await fetchSellerById(id); // Assuming this function exists
        sellerMap.set(id, seller);
      } catch {
        // Seller might be deleted or inaccessible
      }
    }),
  ]);

  // For auction orders with total_amount=0, fetch auction price as fallback
  const auctionPriceMap = new Map<string, number>();
  const auctionOrdersNeedingPrice = orders.filter((o) => {
    const shirt = shirtMap.get(o.shirt_id);
    return (!o.total_amount || o.total_amount <= 0) && shirt?.sales_type === "auction";
  });
  if (auctionOrdersNeedingPrice.length > 0) {
    const uniqueAuctionShirtIds = Array.from(
      new Set(auctionOrdersNeedingPrice.map((o) => o.shirt_id))
    );
    await Promise.allSettled(
      uniqueAuctionShirtIds.map(async (shirtId) => {
        try {
          const auction = await auctionService.getAuctionByShirtId(shirtId);
          if (auction.current_price > 0) {
            auctionPriceMap.set(shirtId, auction.current_price);
          }
        } catch {
          // Auction data unavailable
        }
      })
    );
  }

  return orders.map((o) => {
    const shirt = shirtMap.get(o.shirt_id);
    const seller = sellerMap.get(o.seller_id);

    // Fix total_amount when backend returns 0
    let totalAmount = o.total_amount;
    if (!totalAmount || totalAmount <= 0) {
      const auctionPrice = auctionPriceMap.get(o.shirt_id);
      if (auctionPrice && auctionPrice > 0) {
        totalAmount = auctionPrice;
      } else if (shirt?.price != null && shirt.price > 0) {
        totalAmount = shirt.price;
      }
    }

    return { ...o, total_amount: totalAmount, shirt, seller };
  });
}

export interface AdminOrder {
  id: string;
  shirt_id: string;
  shirt_title: string;
  total_amount: number;
  status: "completed" | "pending" | "failed";
  payment_status: "captured" | "pending" | "failed" | "refunded";
  shipping_status: "in_transit" | "delivered" | "pending" | "problem" | "returned";
  created_at: string;
  buyer_id: string;
  buyer_email: string;
  buyer_name: string;
  seller_id: string;
  seller_email: string;
  seller_name: string;
}

/** Veiling gewonnen maar (nog) niet betaald — voor het admin-overzicht. */
export interface UnpaidAuctionWinner {
  auction_id: string;
  shirt_id: string;
  shirt_title: string;
  winner_id: string;
  winner_email: string;
  winner_name: string;
  winning_bid: number;
  won_at: string | null;
  /** 'no_checkout' (nooit gestart) of 'pending'/'authorized' (afgehaakt). */
  payment_state: string;
}

export async function fetchUnpaidAuctionWinners(): Promise<UnpaidAuctionWinner[]> {
  const res = await fetch(`${API_URL}/orders/admin/unpaid-auction-winners`, {
    method: "GET",
    headers: { Accept: "application/json", ...authHeaders() },
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `Unpaid winners fetch failed (${res.status}): ${body.slice(0, 200)}`,
    );
  }
  return res.json();
}

export async function fetchAdminOrders(
  limit = 50,
  offset = 0,
  status?: string
): Promise<AdminOrder[]> {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  params.append("offset", String(offset));
  if (status) params.append("status", status);

  const res = await fetch(`${API_URL}/orders/admin/all?${params.toString()}`, {
    method: "GET",
    headers: { "Accept": "application/json", ...authHeaders() },
    credentials: "include",
  });

  const contentType = res.headers.get("content-type") ?? "";
  if (!res.ok || !contentType.includes("application/json")) {
    const body = await res.text();
    throw new Error(
      `Admin orders fetch failed — status: ${res.status}, content-type: ${contentType}, body: ${body.slice(0, 200)}`
    );
  }

  return res.json();
}
